'use client';

import React, { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode?: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact';
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      ready: (fn: () => void) => void;
    };
  }
}

type TurnstileWidgetProps = {
  siteKey: string;
  onSuccess: (token: string) => void;
  onError?: (errorCode?: string) => void;
  onExpired?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  className?: string;
};

export function TurnstileWidget({
  siteKey,
  onSuccess,
  onError,
  onExpired,
  theme = 'auto',
  size = 'normal',
  className,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onExpiredRef = useRef(onExpired);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (!siteKey?.trim() || typeof document === 'undefined') return;

    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);
    if (existing) {
      if (window.turnstile) setScriptLoaded(true);
      else existing.addEventListener('load', () => setScriptLoaded(true));
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
    return () => {
      // Don't remove script on unmount so other instances can use it
    };
  }, [siteKey]);

  // Render widget only after script has loaded. Do not use turnstile.ready() — it requires
  // the script to be loaded without async/defer and can run before the script is loaded.
  useEffect(() => {
    if (!scriptLoaded || !window.turnstile || !containerRef.current || !siteKey?.trim()) return;

    const container = containerRef.current;
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (_) {}
      widgetIdRef.current = null;
    }

    const id = window.turnstile.render(container, {
      sitekey: siteKey,
      callback: (token) => onSuccessRef.current(token),
      'error-callback': (code) => onErrorRef.current?.(code),
      'expired-callback': () => onExpiredRef.current?.(),
      theme,
      size,
    });
    widgetIdRef.current = id;

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, [scriptLoaded, siteKey, theme, size]);

  if (!siteKey?.trim()) return null;

  return <div ref={containerRef} className={className} />;
}
